import { ArrowUpDown } from "lucide-react";

import { alignment, type Column } from "@/components/admin/table";
import { cn } from "@/lib/utils";

/**
 * A click-sortable column header (POS2-1138 decision 29).
 *
 * `SortHeader` already exists twice, byte-similar, at `pages/inventory.tsx` and
 * `pages/settings/catalogue.tsx`. A third copy is the wrong answer; converting those two is work
 * outside this ticket's PRD. So the shared component is built here with one caller, and the swap of
 * the existing two is filed as a follow-up.
 *
 * **It owns the header affordance and nothing else.** Both existing copies are server-sorted and
 * this one is client-sorted, so the component takes a key and a direction and reports a click — it
 * does not know what sorting means. That is also what lets the customer-health table sort on two
 * keys that are not columns at all (devices as a ratio, health as a score).
 *
 * Alignment comes from the `Column`, never from a prop at the call site, for the same reason `Td`
 * takes one: a header that can be aligned differently from its cells eventually is (POS2-1086).
 *
 * `aria-sort` is deliberately *not* here. ARIA puts it on the `<th>` and a `<button>` cannot carry
 * it, so the state travels through `Column.ariaSort` and the shared table announces it — which is
 * also the only arrangement in which a screen reader hears the sort at all.
 */
export function SortHeader<K extends string>({
  label,
  sortKey,
  active,
  direction,
  column,
  onSort,
}: {
  label: string;
  sortKey: K;
  active: K;
  direction: "asc" | "desc";
  column: Column;
  onSort: (key: K) => void;
}) {
  const isActive = active === sortKey;
  return (
    <button
      type="button"
      // The header cell already carries the column's alignment; this keeps the label and its arrow
      // together against the right edge of a numeric column rather than centred in the cell.
      className={cn(
        "inline-flex w-full cursor-pointer select-none items-center gap-1 uppercase tracking-wide",
        alignment(column) === "text-right" ? "justify-end" : "justify-start",
      )}
      onClick={() => onSort(sortKey)}
    >
      {label}
      <ArrowUpDown
        className={cn(
          "size-3",
          isActive ? "text-gray-900" : "text-gray-300",
          isActive && direction === "desc" && "rotate-180",
        )}
        aria-hidden
      />
    </button>
  );
}

/**
 * Sort `rows` by `key`, with NULLs always last whichever way the arrow points.
 *
 * That rule is not a preference here. Every nullable key on the health table means *absence of
 * evidence* — a site with no labelling, no login, no report — and floating those to the top of an
 * ascending sort would put "we know nothing about this site" where "this site is in trouble"
 * belongs. Worst-first has to mean worst.
 */
export function sortRows<T, K extends string>(
  rows: T[],
  key: K,
  direction: "asc" | "desc",
  keys: Record<K, (row: T) => string | number | null>,
): T[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = keys[key](a);
    const right = keys[key](b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    if (left === right) return 0;
    return (left < right ? -1 : 1) * sign;
  });
}
